import { useState, useEffect } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LoadingOverlay from "../../../core/LoadingOverlay";
import ConfirmDialog from "../../../core/ConfirmDialog";
import DismissibleMessage from "../DismissibleMessage";
import { calibrationService, KMZ_DEFAULT_PROJECT_NAME } from "../../../../services/CalibrationService";
import { ClientsDropdown, ProjectsDropdown } from "../DropdownV2";
import { useStyles } from "./styles";
import { DEFAULT_GROUP_KEY, PLSCADD_KMZ_CONFIGS_DATASET_TYPE, PLSCADD_KMZ_DATASET_TYPE } from "./constants";

export default function ConvertTab() {
    const classes = useStyles();
    const [clientName, setClientName] = useState("");
    const [projectName, setProjectName] = useState("");
    // Available KMZs are keyed as `${name}||${version}` in this set when selected.
    const [kmzRows, setKmzRows] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [kmzsLoading, setKmzsLoading] = useState(false);
    // The latest config version stored for the selected client + project, shown so
    // the user knows which config the conversion will apply. null = none found.
    const [configVersion, setConfigVersion] = useState(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Load the available PLS-CADD KMZs (name + version) for the selected client
    // from the reserved "kmz" project. Groups come from getGroups; each group's
    // versions come from getFileNames.
    useEffect(() => {
        if (!clientName) {
            setKmzRows([]);
            setSelectedKeys(new Set());
            return;
        }
        let cancelled = false;
        const loadKmzs = async () => {
            setKmzsLoading(true);
            try {
                const groupsResponse = await calibrationService.getGroups(
                    clientName,
                    KMZ_DEFAULT_PROJECT_NAME,
                    PLSCADD_KMZ_DATASET_TYPE
                );
                const groups = groupsResponse?.data || [];
                const perGroup = await Promise.all(
                    groups.map(async (group) => {
                        const filesResponse = await calibrationService.getFileNames(
                            clientName,
                            KMZ_DEFAULT_PROJECT_NAME,
                            group,
                            PLSCADD_KMZ_DATASET_TYPE
                        );
                        const versions = filesResponse?.data || [];
                        return versions.map((version) => ({ name: group, version }));
                    })
                );
                if (!cancelled) {
                    const rows = perGroup.flat().sort((a, b) => {
                        if (a.name !== b.name) return a.name.localeCompare(b.name);
                        return b.version.localeCompare(a.version);
                    });
                    setKmzRows(rows);
                    setSelectedKeys(new Set());
                }
            } catch (err) {
                if (!cancelled) {
                    setErrorMessage(`Failed to load KMZs: ${err}`);
                    setKmzRows([]);
                }
            } finally {
                if (!cancelled) {
                    setKmzsLoading(false);
                }
            }
        };
        loadKmzs();
        return () => {
            cancelled = true;
        };
    }, [clientName]);

    // Load the latest config stored for the selected client + project so we can
    // show which config version the conversion will apply. Configs are per
    // client-project, so this waits until both are selected.
    useEffect(() => {
        if (!clientName || !projectName) {
            setConfigVersion(null);
            return;
        }
        let cancelled = false;
        const loadConfigVersion = async () => {
            setConfigLoading(true);
            try {
                const latest = await calibrationService
                    .getLatestDataset(
                        clientName,
                        projectName,
                        DEFAULT_GROUP_KEY,
                        PLSCADD_KMZ_CONFIGS_DATASET_TYPE,
                        false
                    )
                    .catch(() => null); // 404 -> no config saved for this project yet
                if (!cancelled) {
                    setConfigVersion(latest?.meta?.version_dttm ?? null);
                }
            } finally {
                if (!cancelled) {
                    setConfigLoading(false);
                }
            }
        };
        loadConfigVersion();
        return () => {
            cancelled = true;
        };
    }, [clientName, projectName]);

    const rowKey = (row) => `${row.name}||${row.version}`;

    const toggleRow = (row) => {
        const key = rowKey(row);
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const allSelected = kmzRows.length > 0 && selectedKeys.size === kmzRows.length;
    const someSelected = selectedKeys.size > 0 && !allSelected;

    const toggleSelectAll = () => {
        setSelectedKeys(allSelected ? new Set() : new Set(kmzRows.map(rowKey)));
    };

    const handleSubmit = () => {
        setConfirmModalOpen(false);
        setSubmitting(true);
        const inputs = kmzRows
            .filter((row) => selectedKeys.has(rowKey(row)))
            .map((row) => ({
                dataset_type: PLSCADD_KMZ_DATASET_TYPE,
                group_key: row.name,
                version_dttm: row.version,
            }));
        // Fire and forget -- a multi-KMZ run can exceed API Gateway's 29s timeout,
        // but the conversion runs regardless. Show the Progress Viewer link immediately.
        // Only surface errors that happen before the request leaves the browser.
        calibrationService.plscaddKmzToRootGeojson(clientName, projectName, inputs).catch((err) => {
            const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
            if (!isTimeout) {
                setErrorMessage(`Failed to submit: ${err}`);
            }
        });
        setStatusMessage("PLS-CADD KMZ to RootGeojson submitted. Check Progress Viewer for status.");
        setSubmitting(false);
        setSelectedKeys(new Set());
    };

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        clientName || ""
    )}&project=${encodeURIComponent(projectName || "")}&processTypes=PlsCaddKmzToRootGeojson`;

    return (
        <div style={{ paddingBottom: "100px" }}>
            <Typography
                variant="body2"
                style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto 20px", color: "#666" }}
            >
                Kick off the PlsCaddKmzToRootGeojson conversion with the PlscaddKmz dataset selected for the client
                and the output project.
            </Typography>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm PLS-CADD KMZ Conversion"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={`Convert ${selectedKeys.size} KMZ(s) for client "${clientName}" into project "${projectName}"?`}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                {statusMessage && (
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                        <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                            View in Progress Viewer
                        </Link>
                    </div>
                )}
                <LoadingOverlay isLoading={submitting} message="Triggering PLS-CADD KMZ to RootGeojson..." />
                <div className={classes.inputRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        setError={setErrorMessage}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <ProjectsDropdown
                        clientName={clientName}
                        value={projectName}
                        setValue={setProjectName}
                        setError={setErrorMessage}
                        freeSolo={true}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                </div>

                {clientName && projectName && (
                    <Typography
                        variant="body2"
                        sx={{ textAlign: "center", marginTop: 2, color: "#666" }}
                    >
                        {configLoading
                            ? "Checking for config..."
                            : configVersion
                            ? `Config version to be used: ${configVersion}`
                            : "No config found for this project — conversion will run with defaults."}
                    </Typography>
                )}

                {clientName && (
                    <Box sx={{ width: "100%", maxWidth: "800px", marginTop: 2 }}>
                        <Typography variant="h6" sx={{ textAlign: "center", marginBottom: 1 }}>
                            Available PLS-CADD KMZs
                        </Typography>
                        {kmzsLoading ? (
                            <Typography sx={{ textAlign: "center", fontStyle: "italic" }}>
                                Loading KMZs...
                            </Typography>
                        ) : kmzRows.length === 0 ? (
                            <Typography sx={{ textAlign: "center", fontStyle: "italic", color: "#666" }}>
                                No PLS-CADD KMZs found for this client. Upload some from the Upload tab.
                            </Typography>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table aria-label="available kmzs">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    onChange={toggleSelectAll}
                                                    inputProps={{ "aria-label": "select all KMZs" }}
                                                />
                                            </TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Version</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {kmzRows.map((row) => {
                                            const key = rowKey(row);
                                            return (
                                                <TableRow key={key} hover>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedKeys.has(key)}
                                                            onChange={() => toggleRow(row)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{row.name}</TableCell>
                                                    <TableCell>{row.version}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}

                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!clientName || !projectName || selectedKeys.size === 0 || submitting}
                        onClick={() => setConfirmModalOpen(true)}
                    >
                        Run Conversion on Selected KMZs
                    </Button>
                </div>
            </Form>
        </div>
    );
}
